/// <reference types="applepayjs" />
import { cn } from '@tebuto/lib/utils'
import type { WalletCheckoutPricing } from '@tebuto/payment/paypal/wallet-checkout-pricing'
import { useCallback, useEffect, useRef, useState } from 'react'

const APPLE_PAY_VERSION = 14
/** Match Google Pay native wallet artwork height inside the 45px widget frame. */
const APPLE_PAY_BUTTON_CONTENT_HEIGHT_PX = 32

declare global {
    interface Window {
        ApplePaySession: typeof ApplePaySession
    }

    interface HTMLApplePayButtonElement extends HTMLElement {
        buttonstyle?: 'black' | 'white' | 'white-outline'
        type?:
            | 'buy'
            | 'checkout'
            | 'book'
            | 'donate'
            | 'order'
            | 'subscribe'
            | 'add-money'
            | 'contribute'
            | 'rent'
            | 'support'
            | 'tip'
            | 'top-up'
        locale?: string
    }
}

declare module 'react' {
    namespace JSX {
        interface IntrinsicElements {
            'apple-pay-button': {
                buttonstyle?: 'black' | 'white' | 'white-outline'
                type?:
                    | 'buy'
                    | 'checkout'
                    | 'book'
                    | 'donate'
                    | 'order'
                    | 'subscribe'
                    | 'add-money'
                    | 'contribute'
                    | 'rent'
                    | 'support'
                    | 'tip'
                    | 'top-up'
                    | 'set'
                    | 'plain'
                    | 'continue'
                locale?: string
                [key: string]: any
            }
        }
    }
}

interface ApplePayButtonProps {
    onCreateOrder: () => Promise<string>
    onApprove: (data: { orderID: string }) => Promise<void>
    onError: (error: any) => void
    disabled: boolean
    pricing: WalletCheckoutPricing
    onReady?: () => void
    useWidgetButton?: boolean
}

export default function ApplePayButton({
    onCreateOrder,
    onApprove,
    onError,
    disabled,
    pricing,
    onReady,
    useWidgetButton = false
}: ApplePayButtonProps) {
    const buttonRef = useRef<HTMLApplePayButtonElement>(null)
    const [isScriptLoaded, setIsScriptLoaded] = useState(false)
    const [isInitialized, setIsInitialized] = useState(false)
    const [renderButton, setRenderButton] = useState<boolean | null>(null)

    useEffect(() => {
        const loadApplePayScript = () => {
            const scriptUrl = 'https://applepay.cdn-apple.com/jsapi/1.latest/apple-pay-sdk.js'

            if (document.querySelector(`script[src="${scriptUrl}"]`)) {
                setIsScriptLoaded(true)
                return
            }

            const script = document.createElement('script')
            script.src = scriptUrl
            script.async = true

            script.onload = () => {
                setIsScriptLoaded(true)
            }

            script.onerror = () => {
                console.error('Failed to load Apple Pay script')
                onError(new Error('Failed to load Apple Pay script'))
            }

            document.head.appendChild(script)
        }

        loadApplePayScript()
    }, [onError])

    useEffect(() => {
        if (!isScriptLoaded) {
            return
        }

        const initializeApplePay = async () => {
            try {
                const payPalApplePayInstance = (window.paypal as any)?.Applepay()
                if (!payPalApplePayInstance || !window.ApplePaySession) {
                    setTimeout(initializeApplePay, 1000)
                    return
                }

                if (isInitialized) {
                    return
                }

                setIsInitialized(true)

                if (
                    !window.ApplePaySession.supportsVersion(APPLE_PAY_VERSION) ||
                    !window.ApplePaySession.canMakePayments()
                ) {
                    setRenderButton(false)
                    onReady?.()
                    return
                }

                const { isEligible, countryCode, currencyCode, merchantCapabilities, supportedNetworks } =
                    await payPalApplePayInstance.config()
                if (!isEligible) {
                    setRenderButton(false)
                    onReady?.()
                    return
                }

                setRenderButton(true)
                addOnClickHandler(
                    payPalApplePayInstance,
                    countryCode,
                    currencyCode,
                    merchantCapabilities,
                    supportedNetworks
                )
                onReady?.()
            } catch (error) {
                console.error('Apple Pay initialization error:', error)
                onError(error)
                // Call onReady even on error to not block loading
                onReady?.()
            }
        }

        initializeApplePay()
    }, [isScriptLoaded, onCreateOrder, onApprove, onError, disabled, pricing, isInitialized, onReady])

    const addOnClickHandler = useCallback(
        (
            payPalApplePayInstance: any,
            countryCode: string,
            currencyCode: string,
            merchantCapabilities: ApplePayJS.ApplePayMerchantCapability[],
            supportedNetworks: string[]
        ) => {
            async function onClick() {
                const paymentRequest = {
                    countryCode,
                    currencyCode,
                    merchantCapabilities,
                    supportedNetworks,
                    requiredBillingContactFields: ['name' as const, 'postalAddress' as const],
                    requiredShippingContactFields: [],
                    total: {
                        label: 'Demo (Card is not charged)',
                        amount: pricing.grossPrice,
                        type: 'final' as const
                    }
                }

                const session = new window.ApplePaySession(APPLE_PAY_VERSION, paymentRequest)

                session.onvalidatemerchant = (event) => {
                    payPalApplePayInstance
                        .validateMerchant({ validationUrl: event.validationURL })
                        .then((payload: any) => session.completeMerchantValidation(payload.merchantSession))
                        .catch((err: any) => {
                            onError('Apple Pay Händler Validierung fehlgeschlagen')
                            console.error(err)
                            session.abort()
                        })
                }

                session.onpaymentmethodselected = () => {
                    session.completePaymentMethodSelection({
                        newTotal: paymentRequest.total
                    })
                }

                session.onpaymentauthorized = async (event) => {
                    try {
                        const orderId = await onCreateOrder()

                        await payPalApplePayInstance.confirmOrder({
                            orderId,
                            token: event.payment.token,
                            billingContact: event.payment.billingContact,
                            shippingContact: event.payment.shippingContact
                        })

                        onApprove({ orderID: orderId })

                        session.completePayment({
                            status: window.ApplePaySession.STATUS_SUCCESS
                        })
                    } catch (err) {
                        console.error(err)
                        session.completePayment({
                            status: window.ApplePaySession.STATUS_FAILURE
                        })
                    }
                }

                session.oncancel = () => {
                    console.log('Apple Pay payment cancelled')
                }

                session.begin()
            }

            if (buttonRef.current) {
                buttonRef.current.onclick = onClick
            }
        },
        [buttonRef, window.ApplePaySession, onCreateOrder, onApprove, onError, pricing]
    )

    const applePayButtonHeightPx = useWidgetButton ? APPLE_PAY_BUTTON_CONTENT_HEIGHT_PX : 45

    if (renderButton === false) {
        return
    }

    return (
        <div
            className={cn(
                'w-full min-w-0',
                useWidgetButton ? 'widget-paypal-apple-pay-slot min-h-[45px]' : 'min-h-[45px]',
                disabled ? 'pointer-events-none opacity-50' : '',
                renderButton ? '' : 'hidden'
            )}
            style={
                {
                    '--apple-pay-button-height': `${applePayButtonHeightPx}px`
                } as React.CSSProperties
            }
        >
            <apple-pay-button
                ref={buttonRef}
                buttonstyle={useWidgetButton ? 'white' : 'white-outline'}
                type="book"
                locale="de"
                style={{
                    width: '100%',
                    height: `${applePayButtonHeightPx}px`,
                    display: 'block',
                    boxSizing: 'border-box'
                }}
            />
        </div>
    )
}
